import { Observable } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import {
  SnCadIncident,
  SnCreateIncidentRequestBody,
  SnIncidentRequest,
  SnIncidentRequestsParams,
  SnIncidentRequestsResponse,
  SnIncidentsParams,
  SnIncidentsResponse,
  SnUpdateIncidentRequestBody,
} from 'libs/api/src/lib/interfaces/incident.interface';

@Injectable({ providedIn: 'root' })
export class SnIncidentApiService {
  public readonly INCIDENT_URL_PREFIX = '/api/partner/v1/incidents/';
  public readonly INCIDENT_REQUEST_URL_PREFIX = '/api/partner/v1/incident-requests/';

  constructor(private readonly _httpClient: HttpClient) {}

  /**
   * Retrieve a specific incident's details
   * @param incidentId - Unique identifier of the incident
   * @returns Observable with the incident details
   */
  getIncident(incidentId: string): Observable<SnCadIncident> {
    return this._httpClient.get<SnCadIncident>(`${this.INCIDENT_URL_PREFIX}${incidentId}/`);
  }

  /**
   * Search for incidents
   * @param params - Optional search parameters
   * @returns Observable with the list of incidents and pagination information
   */
  getIncidents(params?: SnIncidentsParams): Observable<SnIncidentsResponse> {
    return this._httpClient.get<SnIncidentsResponse>(this.INCIDENT_URL_PREFIX, {
      params: params as unknown as { [param: string]: string | number | boolean },
    });
  }

  /**
   * Search for incident requests
   * @param params - Optional search parameters
   * @returns Observable with the list of incident requests and pagination information
   */
  getIncidentRequests(params?: SnIncidentRequestsParams): Observable<SnIncidentRequestsResponse> {
    return this._httpClient.get<SnIncidentRequestsResponse>(this.INCIDENT_REQUEST_URL_PREFIX, {
      params: params as unknown as { [param: string]: string | number | boolean },
    });
  }

  /**
   * Retrieve a specific incident request's details
   * @param requestId - Unique identifier of the incident request
   * @returns Observable with the incident request details
   */
  getIncidentRequest(requestId: string): Observable<SnIncidentRequest> {
    return this._httpClient.get<SnIncidentRequest>(
      `${this.INCIDENT_REQUEST_URL_PREFIX}${requestId}/`,
    );
  }

  /**
   * Update an incident request
   * @param requestId - Unique identifier of the incident request to update
   * @param body - Fields to update
   * @returns Observable with the updated incident request
   */
  updateIncidentRequest(
    requestId: string,
    body: SnUpdateIncidentRequestBody,
  ): Observable<SnIncidentRequest> {
    return this._httpClient.patch<SnIncidentRequest>(
      `${this.INCIDENT_REQUEST_URL_PREFIX}${requestId}/`,
      body,
    );
  }

  /**
   * Delete an incident request
   * @param requestId - Unique identifier of the incident request to delete
   * @returns Observable with empty response
   */
  deleteIncidentRequest(requestId: string): Observable<void> {
    return this._httpClient.delete<void>(`${this.INCIDENT_REQUEST_URL_PREFIX}${requestId}/`);
  }

  /**
   * Retrieve all requests for a specific incident
   * @param incidentId - Unique identifier of the incident
   * @returns Observable with the list of incident requests
   */
  getIncidentRequestsForIncident(incidentId: string): Observable<SnIncidentRequest[]> {
    return this._httpClient.get<SnIncidentRequest[]>(
      `${this.INCIDENT_URL_PREFIX}${incidentId}/requests/`,
    );
  }

  /**
   * Create a new incident request
   * @param incidentId - Unique identifier of the incident
   * @param body - New incident request data
   * @returns Observable with the created incident request
   */
  createIncidentRequest(
    incidentId: string,
    body: SnCreateIncidentRequestBody,
  ): Observable<SnIncidentRequest> {
    return this._httpClient.post<SnIncidentRequest>(
      `${this.INCIDENT_URL_PREFIX}${incidentId}/requests/`,
      body,
    );
  }
}
